import React, { useState, useRef } from 'react';
import { X, Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { investorAccountAPI } from '../../services/api';

const TxnNavBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = async () => {
    try {
      const response = await investorAccountAPI.downloadTxnNavTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transactions_nav_bulk_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template');
    }
  };

  const handleDownloadAccountRefList = async () => {
    try {
      const response = await investorAccountAPI.downloadAccountReferenceList();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'investor_account_reference_list.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download account reference list');
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && selected.name.endsWith('.csv')) {
      setFile(selected);
      setError('');
      setResult(null);
    } else {
      setError('Please select a CSV file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const response = await investorAccountAPI.bulkUploadTxnNav(file);
      setResult(response.data);
      if (response.data.transactions_created > 0 || response.data.navs_created > 0) {
        onSuccess?.();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="txn-nav-bulk-upload-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Transactions & FMV/NAV</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600" data-testid="close-txn-nav-modal">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Upload transactions and FMV/NAV records across all investor accounts. Each row
            identifies the account (by <strong>Account Ref No → PAN → Account Name</strong>) and fund, then provides transaction and/or NAV data.
          </p>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 leading-relaxed" data-testid="txn-nav-help">
            <strong>New:</strong> Use the new <code className="px-1 bg-blue-100 rounded">Account Ref No</code> column for precise matching (download the Account Reference List below). PAN and Account Name remain as fallbacks. Rows where <code className="px-1 bg-blue-100 rounded">Transaction Type = Drawdown Received</code> require a{' '}
            <code className="px-1 bg-blue-100 rounded">Drawdown Number</code> and are saved as{' '}
            <strong>pending-approval receipt tranches</strong> on the matching drawdown until maker-checker approves them.
          </div>
          <p className="text-xs text-gray-500">
            A single row can create both a transaction <strong>and</strong> a NAV record if both
            sets of fields are filled. Leave transaction or NAV columns empty to skip that type.
          </p>

          {/* Downloads */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="download-txn-nav-template"
            >
              <Download size={16} />
              Download CSV Template
            </button>
            <button
              onClick={handleDownloadAccountRefList}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
              data-testid="download-txn-nav-account-ref-list"
              title="Download list of existing Investor Accounts with their Ref Nos so you can paste them into the Account Ref No column"
            >
              <Download size={16} />
              Download Account Reference List
            </button>
          </div>

          {/* File Input */}
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="txn-nav-file-drop"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              data-testid="txn-nav-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <FileText size={20} className="text-blue-500" />
                <span>{file.name}</span>
                <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <div>
                <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-500">Click to select a CSV file</p>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="txn-nav-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-4 bg-gray-50 border rounded-lg space-y-2" data-testid="txn-nav-result">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle size={16} />
                Upload Complete
              </div>
              <div className="grid grid-cols-4 gap-2 text-sm">
                <div className="bg-white p-2 rounded border text-center">
                  <div className="text-lg font-semibold text-blue-600">{result.transactions_created}</div>
                  <div className="text-gray-500 text-xs">Transactions</div>
                </div>
                <div className="bg-white p-2 rounded border text-center">
                  <div className="text-lg font-semibold text-amber-600">{result.receipts_pending_approval || 0}</div>
                  <div className="text-gray-500 text-xs">Pending Approval</div>
                </div>
                <div className="bg-white p-2 rounded border text-center">
                  <div className="text-lg font-semibold text-green-600">{result.navs_created}</div>
                  <div className="text-gray-500 text-xs">NAV Records</div>
                </div>
                <div className="bg-white p-2 rounded border text-center">
                  <div className="text-lg font-semibold text-red-600">{result.failed}</div>
                  <div className="text-gray-500 text-xs">Failed</div>
                </div>
              </div>
              {(result.receipts_pending_approval || 0) > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                  Open the relevant Drawdown's detail page (Investors tab) or the Investor Account's Transactions tab to Approve / Reject the pending receipts.
                </p>
              )}
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-gray-700 mb-1">Errors:</p>
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-txn-nav-btn"
            >
              {uploading ? 'Uploading...' : 'Upload & Process'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TxnNavBulkUploadModal;
